/**
 * Foundation Home Cards - registry riutilizzabile per le "Card Operative"
 * mostrate nella Home del Centro Guida.
 *
 * Il registry e' data-driven: nessun testo, icona, ruolo, percorso o Pack
 * viene hardcoded nei componenti. Ogni modulo della Suite (GI Presenze, GI
 * Studio, GI Fatturazione, GI Finance, CRM, ...) puo' registrare card
 * aggiuntive senza modificare la Foundation.
 *
 * Schema Card:
 *   {
 *     id: string,               // univoco globale ("foundation.<slug>")
 *     order: number,             // ordinamento crescente (default 1000)
 *     title: string,             // titolo breve (max 3 parole)
 *     description: string,       // sottotitolo (1 riga, ~80 caratteri)
 *     icon: string,              // nome icona lucide-react
 *     to: string,                // destinazione (route interna)
 *     roles: string[],           // ruoli abilitati o ["*"] per tutti
 *     pack: string,              // "foundation" o app_key del Pack
 *   }
 */

// Card di default della Foundation. Sono progettate per essere valide in
// tutti i moduli della Suite: rappresentano l'ingresso standard alla
// Guida (Guida rapida, Checklist, Novita', Primi Passi, Assistente AI,
// Feedback).
const DEFAULT_HOME_CARDS = [
  {
    id: "foundation.guide-rapide",
    order: 10,
    title: "Guida Rapida",
    description: "Accedi subito alle procedure operative piu' utilizzate.",
    icon: "BookOpen",
    to: "/guida?topic=guide-rapide",
    roles: ["*"],
    pack: "foundation",
  },
  {
    id: "foundation.checklist",
    order: 20,
    title: "Checklist",
    description: "Controlla lo stato delle attivita' e completa cio' che manca.",
    icon: "CheckSquare",
    to: "/guida?topic=checklist",
    roles: ["*"],
    pack: "foundation",
  },
  {
    id: "foundation.novita",
    order: 30,
    title: "Novita'",
    description: "Scopri le nuove funzionalita' introdotte nell'ultima versione.",
    icon: "Sparkles",
    to: "/guida?topic=novita-versione",
    roles: ["*"],
    pack: "foundation",
  },
  {
    id: "foundation.primi-passi",
    order: 40,
    title: "Primi Passi",
    description: "Configura correttamente la Suite e inizia a lavorare.",
    icon: "Rocket",
    to: "/guida?topic=onboarding",
    roles: ["admin", "super_admin", "studio_operator"],
    pack: "foundation",
  },
  {
    id: "foundation.assistente-ai",
    order: 50,
    title: "Assistente AI",
    description: "L'assistente intelligente sara' disponibile in una prossima release.",
    icon: "Bot",
    to: "/guida?topic=assistente-ai",
    roles: ["*"],
    pack: "foundation",
  },
  {
    id: "foundation.feedback",
    order: 60,
    title: "Feedback",
    description: "Invia suggerimenti e aiutaci a migliorare la Suite.",
    icon: "MessageSquareMore",
    to: "/guida?topic=feedback",
    roles: ["*"],
    pack: "foundation",
  },
];

// Registry mutabile: mappa id -> Card. Le Card di default sono precaricate.
const _registry = new Map();
DEFAULT_HOME_CARDS.forEach((c) => _registry.set(c.id, c));

/** Registra o sovrascrive una Card. */
export function registerHomeCard(card) {
  if (!card || !card.id) throw new Error("HomeCard: id obbligatorio");
  _registry.set(card.id, { order: 1000, roles: ["*"], pack: "foundation", ...card });
}

/** Rimuove una Card dal registry (utile per test/reset). */
export function unregisterHomeCard(id) {
  _registry.delete(id);
}

/**
 * Ritorna la lista delle Card visibili al ruolo dato (solo Card pubblicate,
 * ordinate per `order` crescente).
 *
 * @param {string} role - ruolo dell'utente corrente (es. "employee").
 * @param {object} opts
 * @param {string[]} [opts.packs] - filtra per Pack di appartenenza.
 */
export function listHomeCards(role, opts = {}) {
  const { packs } = opts;
  const all = Array.from(_registry.values());
  return all
    .filter((c) => (packs ? packs.includes(c.pack) : true))
    .filter((c) => {
      const roles = c.roles || ["*"];
      if (roles.includes("*")) return true;
      return roles.includes(role);
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/** Numero di Card registrate (utile per test). */
export function countHomeCards() {
  return _registry.size;
}

// Esporto anche i default per uso in test.
export { DEFAULT_HOME_CARDS };
